import { useMemo } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  FundingRequestEntitiesFragmentGQLType,
  PrincipalPaymentMetricsGQLType,
} from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

export const useCalculateMinFundTransferDate = (params: {
  principalPaymentMetrics: PrincipalPaymentMetricsGQLType | null;
  fundingRequest: FundingRequestEntitiesFragmentGQLType;
}): Date => {
  const { principalPaymentMetrics, fundingRequest } = params;

  const flags = useFeatureFlags();

  const minSelectableDate = useMemo<Date>(() => {
    // If admin agent fronting is not enabled, allow any date to be selected
    // since that means "AdminAgentPrincipalPayable" is not in use and these
    // dates basically don't have any accounting impacts (just pure marker)
    if (!commonUtils.flags.isAdminAgentFrontingEnabled) {
      return new Date();
    }

    if (flags.productsInvoicesFullBalancePaymentPermissions !== 'allow') {
      return commonDate.fullDateStrToDate(fundingRequest.originationDate);
    }

    // If an invoice has been created, presumably it's older than the
    // the origination date so prioritizing this date as it's later
    // as the "minimum" date that user can select (reduce history impact)
    if (principalPaymentMetrics && principalPaymentMetrics.latestPaymentDate) {
      let earliestComponentDate = principalPaymentMetrics.latestPaymentDate;

      fundingRequest.fundingComponents.forEach((component) => {
        if (
          component.type === 'drawRequest' &&
          component.targetFundingDate < earliestComponentDate
        ) {
          earliestComponentDate = component.targetFundingDate;
        }
      });

      return commonDate.fullDateStrToDate(earliestComponentDate);
    }

    return commonDate.fullDateStrToDate(fundingRequest.originationDate);
  }, [
    principalPaymentMetrics,
    fundingRequest.originationDate,
    flags.productsInvoicesFullBalancePaymentPermissions,
    fundingRequest.fundingComponents,
  ]);

  return minSelectableDate;
};
